import { useEffect, useState } from "react";
import { View } from "react-native";
import { ActivityIndicator } from "react-native-paper";
import { Button, ScreenContainer, ScrollableContainer, ActionsContainer } from "../../components";
import Instructions from "./Instructions";
import JobSearchStatus from "./JobSearchStatus";
import JobNotificationPreferences from "./JobNotificationPreferences";
import JobCategories from "./JobCategories";
import MinimumCompensation from "./MinimumCompensation";
import Locations from "./Locations";
import OtherPreferences from "./OtherPreferences";
import LocationsModal from "./LocationsModal";
import { useQuery, useMutation, useApolloClient } from "@apollo/client";
import { JOB_PREFERENCE_DATA } from "./queries";
import { SET_JOB_PREFERENCE } from "./mutations";
import { V3_CLIENT_NAME } from "../../components/GraphqlProvider";
import { useForm } from "react-hook-form";
import UnsavedChangesModal from "../../components/UnsavedChangesModal";
import { ScreenProps } from "../types";
import useSnackBar from "../../hooks/useSnackBar";
import useIntercom from "../../hooks/useIntercom";
import config from "../../config";

import { JobPreferenceField, JobPreferenceDataQuery, JobPreferencesFormData, JobPreference } from "./types";

interface MutationData {
  setJobPreference: JobPreference;
}

const JobPreferencesScreen = ({ navigation }: ScreenProps<"JobPreferences">) => {
  const [searchStatuses, setSearchStatuses] = useState<JobPreferenceField[]>([]);
  const [categories, setCategories] = useState<JobPreferenceField[]>([]);
  const [locations, setLocations] = useState<JobPreferenceField[]>([]);
  const [minimumCompensation, setMinimumCompensation] = useState<JobPreferenceField[]>([]);

  const [locationsModalVisible, setLocationsModalVisible] = useState<boolean>(false);
  const [showUnsavedChangesModal, setShowUnsavedChangesModal] = useState<boolean>(false);
  const [discardChanges, setDiscardChanges] = useState<boolean>(false);
  const [shouldGoBack, setShouldGoBack] = useState<boolean>(false);

  const showSnackBar = useSnackBar();
  const intercom = useIntercom();

  const {
    control,
    handleSubmit,
    setValue,
    getValues,
    reset,
    formState: { isDirty },
  } = useForm<JobPreferencesFormData>();

  const formValues = getValues();

  const client = useApolloClient();

  const handleSuccess = (mutationData?: MutationData) => {
    if (isDirty) reset({}, { keepValues: true });
    const style = shouldGoBack ? {} : { marginBottom: 100 };
    showSnackBar({ message: "Preferences Saved", icon: "check-circle", duration: 1500, style });

    if (mutationData) {
      client.cache.updateQuery({ query: JOB_PREFERENCE_DATA }, (data) => {
        if (!data.me.jobPreference) {
          return { ...data, me: { ...data.me, jobPreference: mutationData.setJobPreference } };
        }
      });
    }
    if (shouldGoBack) return navigation.goBack();
  };

  const { data, loading } = useQuery<JobPreferenceDataQuery>(JOB_PREFERENCE_DATA, { context: { clientName: V3_CLIENT_NAME } });
  const [setJobPreference, { loading: mutationLoading }] = useMutation<MutationData>(SET_JOB_PREFERENCE, {
    context: { clientName: V3_CLIENT_NAME },
    onCompleted: handleSuccess,
  });

  useEffect(() => {
    if (!data) return;
    const { jobPreferenceFields, me } = data;

    setSearchStatuses(jobPreferenceFields.jobSearchStatus);
    setCategories(jobPreferenceFields.jobCategories);
    setLocations(jobPreferenceFields.locations);
    setMinimumCompensation(jobPreferenceFields.minimumCompensation);

    if (me.jobPreference) {
      setValue("jobSearchStatus", me.jobPreference.jobSearchStatus);
      setValue("minimumCompensation", me.jobPreference.minimumCompensation?.toString());
      setValue("openToSeasonal", me.jobPreference.openToSeasonal);
      setValue("optedInToJobSeekerEmails", me.jobPreference.optedInToJobSeekerEmails);
      setValue("openToInternational", me.jobPreference.openToInternational);
      setValue(
        "jobCategories",
        me.jobPreference.jobCategories.reduce((acc, curr) => curr && { ...acc, [curr]: true }, {})
      );
      setValue(
        "jobLocations",
        me.jobPreference.jobLocations.reduce((acc, curr) => curr && { ...acc, [curr]: true }, {})
      );
    }
  }, [data]);

  useEffect(() => {
    if (discardChanges) return navigation.goBack();
    const unsubscribe = navigation.addListener("beforeRemove", (event) => {
      if (isDirty) {
        event.preventDefault();
        setShowUnsavedChangesModal(true);
      }
    });

    return unsubscribe;
  }, [navigation, isDirty, discardChanges]);

  const discardAndExit = () => {
    setDiscardChanges(true);
  };

  const saveAndExit = () => {
    setShouldGoBack(true);
    handleSubmit(onSubmit)();
  };

  const onSubmit = (formData: JobPreferencesFormData) => {
    if (!isDirty) return handleSuccess();
    const input = {
      jobSearchStatus: formData.jobSearchStatus,
      jobCategories: Object.keys(formData.jobCategories || {}).filter((key) => formData.jobCategories[key]),
      jobLocations: Object.keys(formData.jobLocations || {}).filter((key) => formData.jobLocations[key]),
      minimumCompensation: formData.minimumCompensation,
      openToSeasonal: formData.openToSeasonal,
      optedInToJobSeekerEmails: formData.optedInToJobSeekerEmails,
      openToInternational: formData.openToInternational,
    };

    setJobPreference({ variables: { input } });
  };

  const onMoreDetails = () => {
    intercom.present(config.intercomContent.howDoJobPreferencesWork);
  };

  if (loading) {
    return (
      <View className="flex-1 items-center justify-center pt-10 bg-white">
        <ActivityIndicator testID="job-preferences-screen-activity-indicator" />
      </View>
    );
  }

  return (
    <ScreenContainer>
      <ScrollableContainer>
        <View className="space-y-1 border-t border-sky-light bg-sky-lighter">
          <Instructions
            {...{
              onMoreDetails,
            }}
          />
          <JobSearchStatus
            {...{
              control,
              searchStatuses,
            }}
          />
          <JobNotificationPreferences
            {...{
              control,
            }}
          />
          <JobCategories
            {...{
              control,
              categories,
            }}
          />
          <MinimumCompensation
            {...{
              control,
              minimumCompensation,
            }}
          />
          <Locations
            {...{
              formValues,
              setLocationsModalVisible,
            }}
          />
          <LocationsModal
            {...{
              control,
              locations,
              visible: locationsModalVisible,
              setVisible: setLocationsModalVisible,
            }}
          />
          <OtherPreferences
            {...{
              control,
            }}
          />
          <UnsavedChangesModal
            {...{
              mutationLoading,
              showUnsavedChangesModal,
              setShowUnsavedChangesModal,
              discardAndExit,
              handleSubmit: saveAndExit,
            }}
          />
        </View>
      </ScrollableContainer>
      <ActionsContainer>
        <Button loading={mutationLoading} disabled={mutationLoading} onPress={handleSubmit(onSubmit)} mode="contained">
          Save Preferences
        </Button>
      </ActionsContainer>
    </ScreenContainer>
  );
};

export default JobPreferencesScreen;
